import { useEffect, type RefObject } from 'react';

/** Whole gesture, first finger down to last finger up. */
const TAP_MS = 350;
/** All fingers of a tap land within this window (a resting palm arrives more slowly). */
const STAGGER_MS = 150;
/** A finger that travels further than this is a pan or pinch, not a tap. */
const MOVE_PX = 12;

function isTextTarget(t: EventTarget | null): boolean {
  if (!(t instanceof HTMLElement)) return false;
  return t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable;
}

/**
 * Detects two- and three-finger taps on the viewport (undo / redo on touch screens).
 *
 * Works on raw touch events in the capture phase, so it sees every finger regardless of which
 * element captured its pointer (the draw canvas, an item, the viewport). A tap counts when
 * every finger lands within a short window of the first, none of them travels, all lift
 * within TAP_MS, and no pen is on the screen (fingers beside a working pen are a palm).
 */
export function useFingerTaps(ref: RefObject<HTMLElement | null>, onTap: (fingers: number) => void): void {
  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const starts = new Map<number, { x: number; y: number }>();
    let startAt = 0;
    let fingers = 0;
    let ok = false;
    let pens = 0;

    const reset = () => {
      starts.clear();
      fingers = 0;
      ok = false;
    };

    const onStart = (e: TouchEvent) => {
      const now = performance.now();
      // Every touch is new: a fresh gesture (also recovers if an earlier end never reached us).
      if (e.touches.length === e.changedTouches.length || !starts.size) {
        reset();
        startAt = now;
        const t = e.changedTouches[0]?.target as Element | null;
        ok = !!t?.closest?.('.viewport') && !isTextTarget(t) && !t.closest('.sel-box') && pens === 0;
      } else if (now - startAt > STAGGER_MS || pens > 0) {
        ok = false;
      }
      for (const t of Array.from(e.changedTouches)) starts.set(t.identifier, { x: t.clientX, y: t.clientY });
      fingers = Math.max(fingers, e.touches.length);
    };

    const onMove = (e: TouchEvent) => {
      if (!ok) return;
      for (const t of Array.from(e.changedTouches)) {
        const s = starts.get(t.identifier);
        if (s && Math.hypot(t.clientX - s.x, t.clientY - s.y) > MOVE_PX) {
          ok = false;
          return;
        }
      }
    };

    const onEnd = (e: TouchEvent) => {
      for (const t of Array.from(e.changedTouches)) starts.delete(t.identifier);
      if (e.type === 'touchcancel') ok = false;
      if (e.touches.length) return;
      const tap = ok && (fingers === 2 || fingers === 3) && performance.now() - startAt < TAP_MS;
      const n = fingers;
      reset();
      if (tap) onTap(n);
    };

    // A pen on the screen turns any finger into a palm.
    const onPointerDown = (e: PointerEvent) => {
      if (e.pointerType === 'pen') pens++;
    };
    const onPointerEnd = (e: PointerEvent) => {
      if (e.pointerType === 'pen') pens = Math.max(0, pens - 1);
    };

    const opts: AddEventListenerOptions = { capture: true, passive: true };
    el.addEventListener('touchstart', onStart, opts);
    el.addEventListener('touchmove', onMove, opts);
    el.addEventListener('touchend', onEnd, opts);
    el.addEventListener('touchcancel', onEnd, opts);
    el.addEventListener('pointerdown', onPointerDown, opts);
    el.addEventListener('pointerup', onPointerEnd, opts);
    el.addEventListener('pointercancel', onPointerEnd, opts);
    return () => {
      el.removeEventListener('touchstart', onStart, opts);
      el.removeEventListener('touchmove', onMove, opts);
      el.removeEventListener('touchend', onEnd, opts);
      el.removeEventListener('touchcancel', onEnd, opts);
      el.removeEventListener('pointerdown', onPointerDown, opts);
      el.removeEventListener('pointerup', onPointerEnd, opts);
      el.removeEventListener('pointercancel', onPointerEnd, opts);
    };
  }, [ref, onTap]);
}

/** A very fast pinch: first finger down to last finger up. A pinch meant to zoom takes longer. */
const PINCH_MS = 300;
/** Both fingers land within this window. */
const PINCH_STAGGER_MS = 100;
/** The fingers end at most this fraction of their starting spread apart... */
const PINCH_RATIO = 0.6;
/** ...and each at least this much nearer their midpoint (px), which also keeps a two-finger tap from counting. */
const PINCH_TRAVEL_PX = 25;

/** Mean distance of the touches from their centroid. */
function spreadOf(touches: TouchList): number {
  const pts = Array.from(touches);
  const cx = pts.reduce((a, t) => a + t.clientX, 0) / pts.length;
  const cy = pts.reduce((a, t) => a + t.clientY, 0) / pts.length;
  return pts.reduce((a, t) => a + Math.hypot(t.clientX - cx, t.clientY - cy), 0) / pts.length;
}

/**
 * Detects a very fast two-finger pinch on the viewport (reset the view on touch screens): two
 * fingers land together, snap together and lift, all within PINCH_MS. The pinch zooms the canvas
 * out on its way, as any pinch does; the reset on lift replaces that. Raw touch events in the
 * capture phase, like useFingerTaps, and likewise off while a pen is on the screen.
 */
export function useFingerPinch(ref: RefObject<HTMLElement | null>, onPinch: () => void): void {
  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    let startAt = 0;
    let ok = false;
    let startSpread = 0;
    let spread = 0;
    let pens = 0;

    const onStart = (e: TouchEvent) => {
      const now = performance.now();
      if (e.touches.length === e.changedTouches.length) {
        startAt = now;
        startSpread = 0;
        const t = e.changedTouches[0]?.target as Element | null;
        ok = !!t?.closest?.('.viewport') && !isTextTarget(t) && !t.closest('.sel-box') && pens === 0;
      }
      if (e.touches.length > 2 || now - startAt > PINCH_STAGGER_MS || pens > 0) ok = false;
      if (ok && e.touches.length === 2) startSpread = spread = spreadOf(e.touches);
    };

    const onMove = (e: TouchEvent) => {
      if (ok && startSpread && e.touches.length === 2) spread = spreadOf(e.touches);
    };

    const onEnd = (e: TouchEvent) => {
      if (e.type === 'touchcancel') ok = false;
      if (e.touches.length) return;
      const pinched =
        ok &&
        startSpread > 0 &&
        spread <= startSpread * PINCH_RATIO &&
        startSpread - spread >= PINCH_TRAVEL_PX &&
        performance.now() - startAt < PINCH_MS;
      ok = false;
      if (pinched) onPinch();
    };

    const onPointerDown = (e: PointerEvent) => {
      if (e.pointerType === 'pen') pens++;
    };
    const onPointerEnd = (e: PointerEvent) => {
      if (e.pointerType === 'pen') pens = Math.max(0, pens - 1);
    };

    const opts: AddEventListenerOptions = { capture: true, passive: true };
    el.addEventListener('touchstart', onStart, opts);
    el.addEventListener('touchmove', onMove, opts);
    el.addEventListener('touchend', onEnd, opts);
    el.addEventListener('touchcancel', onEnd, opts);
    el.addEventListener('pointerdown', onPointerDown, opts);
    el.addEventListener('pointerup', onPointerEnd, opts);
    el.addEventListener('pointercancel', onPointerEnd, opts);
    return () => {
      el.removeEventListener('touchstart', onStart, opts);
      el.removeEventListener('touchmove', onMove, opts);
      el.removeEventListener('touchend', onEnd, opts);
      el.removeEventListener('touchcancel', onEnd, opts);
      el.removeEventListener('pointerdown', onPointerDown, opts);
      el.removeEventListener('pointerup', onPointerEnd, opts);
      el.removeEventListener('pointercancel', onPointerEnd, opts);
    };
  }, [ref, onPinch]);
}
